import { useState, type ReactNode } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  resolveActionIcon,
  resolveActionIdFromLabel,
} from "@/shared/lib/actionIcons";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/core/Button";
import { LoadingOverlay } from "@/shared/ui/core/Loading";
import { ModalContext, useModalContext } from "@/shared/ui/core/modalContext";
import {
  type ModalIconTone,
  type ModalIconVariant,
} from "@/shared/ui/core/Modal";

export type ConfirmationTone =
  | "confirm"
  | "destructive"
  | "unsaved"
  | "conflict";

export type ConfirmationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Optional icon in front of title (kích thước = icon X: size-4). Default matches tone. */
  icon?: ReactNode;
  /** Presentation style: 'badge' or 'inline'. Default 'badge'. */
  iconVariant?: ModalIconVariant;
  /** Color theme for icon badge. Default matches tone. */
  iconTone?: ModalIconTone;
  /** Custom classes for title icon. */
  iconClassName?: string;
  description?: string;
  tone?: ConfirmationTone;
  confirmLabel?: string;
  confirmIcon?: ReactNode;
  cancelLabel?: string;
  cancelIcon?: ReactNode;
  /** `danger` = Hủy đỏ, không viền, cả hai nút cỡ nhỏ. */
  cancelVariant?: "secondary" | "danger";
  loading?: boolean;
  loadingLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  children?: ReactNode;
  /** Whether to wrap children in a card box with border and background (default: false). */
  cardContent?: boolean;
  /** Whether the cancel and confirm buttons span full width across the row (default: true). */
  fullWidthButtons?: boolean;
  /** Keep the dialog open. Used when the installed app must be replaced. */
  hideCancel?: boolean;
  /** Optional custom max-width (e.g. '800px', 'max-w-[800px]', 'max-w-3xl') */
  maxWidth?: string;
  /** Optional custom className for the dialog container */
  className?: string;
};

export const confirmIconBadgeToneClass: Record<ModalIconTone, string> = {
  primary: "bg-primary-soft text-primary border border-primary/20",
  neutral: "bg-bg text-text-secondary border border-border",
  info: "bg-info-soft text-info-fg border border-info/20",
  success: "bg-success-soft text-success-fg border border-success/20",
  warning: "bg-warning-soft text-warning-fg border border-warning/20",
  danger: "bg-danger-soft text-danger-fg border border-danger/25",
};

const defaults: Record<
  ConfirmationTone,
  {
    confirmLabel: string;
    confirmVariant: "primary" | "danger";
    confirmId: string;
    defaultTone: ModalIconTone;
    defaultIcon: ReactNode;
  }
> = {
  confirm: {
    confirmLabel: "Xác nhận",
    confirmVariant: "primary",
    confirmId: "confirm",
    defaultTone: "primary",
    defaultIcon: <CheckCircle2 className="size-7" />,
  },
  destructive: {
    confirmLabel: "Xóa",
    confirmVariant: "danger",
    confirmId: "delete",
    defaultTone: "danger",
    defaultIcon: <AlertTriangle className="size-7" />,
  },
  unsaved: {
    confirmLabel: "Rời trang",
    confirmVariant: "danger",
    confirmId: "back",
    defaultTone: "warning",
    defaultIcon: <AlertTriangle className="size-7" />,
  },
  conflict: {
    confirmLabel: "Tải lại",
    confirmVariant: "primary",
    confirmId: "view",
    defaultTone: "info",
    defaultIcon: <RefreshCw className="size-7" />,
  },
};

function labelIcon(label: string, fallbackId?: string) {
  const id = resolveActionIdFromLabel(label) ?? fallbackId;
  return id ? resolveActionIcon(id, undefined, "size-4") : null;
}

/**
 * Interruptive confirm — Radix AlertDialog (preferred over Dialog + role=alertdialog).
 */
export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = "badge",
  iconTone,
  iconClassName,
  description,
  tone = "confirm",
  confirmLabel,
  confirmIcon,
  cancelLabel = "Hủy",
  cancelIcon,
  cancelVariant = "secondary",
  loading = false,
  loadingLabel,
  onConfirm,
  onCancel,
  children,
  cardContent = false,
  fullWidthButtons = true,
  hideCancel = false,
  maxWidth,
  className,
}: ConfirmationDialogProps) {
  const preset = defaults[tone];
  const confirmText = confirmLabel ?? preset.confirmLabel;
  const btnSize = "md";
  const resolvedTone = iconTone ?? preset.defaultTone;
  const hasCustomTitle = typeof title !== "string";
  const resolvedIcon =
    icon !== undefined ? icon : hasCustomTitle ? null : preset.defaultIcon;
  const parentModal = useModalContext();
  const depth = (parentModal?.depth ?? 0) + 1;
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  const overlayZ = `calc(var(--z-modal) + ${depth * 10})`;
  const contentZ = `calc(var(--z-modal) + ${depth * 10 + 1})`;

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if ((loading || hideCancel) && !next) return;
        onOpenChange(next);
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          style={{ zIndex: overlayZ }}
          className="fixed inset-0 bg-[var(--color-overlay)] animate-in fade-in duration-150"
        />
        <AlertDialog.Content
          ref={setContentElement}
          style={{ zIndex: contentZ }}
          className="fixed inset-0 flex items-center justify-center overflow-visible bg-transparent p-4 outline-none"
          onEscapeKeyDown={
            loading || hideCancel ? (e) => e.preventDefault() : undefined
          }
        >
          {/* Portal SearchableSelect/Popover vào đây — tránh dropdown z-55 nằm dưới dialog z-61 */}
          <ModalContext.Provider value={{ depth, container: contentElement }}>
            <div
              style={
                maxWidth && !maxWidth.startsWith("max-w-")
                  ? { maxWidth }
                  : undefined
              }
              className={cn(
                "relative flex w-full flex-col overflow-hidden",
                maxWidth && maxWidth.startsWith("max-w-")
                  ? maxWidth
                  : maxWidth
                    ? "max-w-[min(100%,calc(100vw-2rem))]"
                    : "max-w-md",
                "max-h-[min(90dvh,48rem)]",
                "rounded-modal border border-border bg-surface p-6 shadow-3",
                "animate-in fade-in zoom-in-95 duration-150",
                className,
              )}
            >
              {/* Top Centered Icon - Clean glyph without surrounding background */}
              {resolvedIcon ? (
                <div
                  className={cn(
                    "mx-auto mb-3.5 flex shrink-0 items-center justify-center select-none [&>svg]:size-12",
                    resolvedTone === "primary" && "text-primary",
                    resolvedTone === "danger" && "text-danger-fg",
                    resolvedTone === "warning" && "text-warning-fg",
                    resolvedTone === "info" && "text-info-fg",
                    resolvedTone === "success" && "text-success-fg",
                    resolvedTone === "neutral" && "text-text-secondary",
                    iconClassName,
                  )}
                  aria-hidden
                >
                  {resolvedIcon}
                </div>
              ) : null}

              {/* Centered Title */}
              {typeof title === "string" ? (
                <AlertDialog.Title className="m-0 text-center text-h3 font-bold text-text leading-snug">
                  {title}
                </AlertDialog.Title>
              ) : (
                <AlertDialog.Title asChild>
                  <div className="m-0 text-center text-h3 font-bold text-text leading-snug">
                    {title}
                  </div>
                </AlertDialog.Title>
              )}

              {/* Centered Description */}
              {description ? (
                <AlertDialog.Description className="mt-2.5 mb-0 text-center text-body text-text-secondary leading-relaxed">
                  {description}
                </AlertDialog.Description>
              ) : (
                <AlertDialog.Description className="sr-only">
                  {typeof title === "string" ? title : "Xác nhận"}
                </AlertDialog.Description>
              )}

              {children ? (
                <div
                  className={cn(
                    "mt-4 min-h-0 flex-1 overflow-y-auto text-left text-body text-text",
                    cardContent &&
                      "rounded-control bg-bg/50 p-3 border border-border/60 text-caption",
                  )}
                >
                  {children}
                </div>
              ) : null}

              {/* Bottom Actions */}
              <div
                className={cn(
                  "mt-6 shrink-0 gap-3",
                  fullWidthButtons
                    ? hideCancel
                      ? "grid grid-cols-1"
                      : "grid grid-cols-2"
                    : "flex items-center justify-end",
                )}
              >
                {hideCancel ? null : (
                  <AlertDialog.Cancel asChild>
                    <Button
                      variant="secondary"
                      size={btnSize}
                      disabled={loading}
                      fullWidth={fullWidthButtons}
                      leftIcon={
                        cancelIcon !== undefined
                          ? cancelIcon
                          : (labelIcon(cancelLabel, "cancel") ?? (
                              <Ban className="size-4" aria-hidden />
                            ))
                      }
                      onClick={() => onCancel?.()}
                    >
                      {cancelLabel}
                    </Button>
                  </AlertDialog.Cancel>
                )}
                <AlertDialog.Action asChild>
                  <Button
                    variant={preset.confirmVariant}
                    size={btnSize}
                    loading={loading}
                    fullWidth={fullWidthButtons}
                    leftIcon={
                      confirmIcon !== undefined
                        ? confirmIcon
                        : labelIcon(confirmText, preset.confirmId)
                    }
                    onClick={(e) => {
                      /** Keep open while async confirm runs (`loading`). */
                      e.preventDefault();
                      onConfirm();
                    }}
                  >
                    {confirmText}
                  </Button>
                </AlertDialog.Action>
              </div>
              <LoadingOverlay
                visible={loading}
                label={
                  loadingLabel ??
                  (tone === "destructive" ? "Đang xóa..." : "Đang xử lý...")
                }
              />
            </div>
          </ModalContext.Provider>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
